<script setup lang="ts">
import CustomCloseButton from '@/components/createdUi/CustomCloseButton.vue'
import CustomSelectedItems from '@/components/createdUi/CustomSelectedItems.vue'
import { getFullName } from '@/utils/formatters'
import type { IStaffDepartmentManager, IStaffDepartmentTreeNode } from '../../types/settings/IStaffDepartmentSettings'

const props = defineProps<{
  modelValue: boolean
  employees: IStaffDepartmentManager[]
  tree: IStaffDepartmentTreeNode[]
  loading?: boolean
}>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  confirm: [departmentId: string]
  remove: [id: string]
  'remove-all': []
}>()

const targetId = ref<string | null>(null)
const collapsedIds = ref<string[]>([])

const selectedItems = computed(() => props.employees.map(item => ({
  id: String(item.id),
  name: item.full_name || getFullName(item) || 'Сотрудник',
  avatar: item.avatar || undefined,
  description: item.position_name || undefined,
})))

const rows = computed(() => {
  const list: Array<{
    id: string
    name: string
    depth: number
    hasChildren: boolean
    expanded: boolean
  }> = []

  function walk(nodes: IStaffDepartmentTreeNode[], depth: number) {
    for (const node of nodes) {
      const id = String(node.id)
      const hasChildren = Boolean(node.children?.length)
      const expanded = hasChildren && !collapsedIds.value.includes(id)
      list.push({ id, name: node.name, depth, hasChildren, expanded })
      if (expanded)
        walk(node.children, depth + 1)
    }
  }

  walk(props.tree, 0)
  return list
})

watch(() => props.modelValue, open => {
  if (!open) return
  targetId.value = null
  collapsedIds.value = collectNestedIds(props.tree)
})

function collectNestedIds(nodes: IStaffDepartmentTreeNode[], depth = 0): string[] {
  return nodes.flatMap(node => {
    const id = String(node.id)
    const nested = collectNestedIds(node.children || [], depth + 1)
    return depth > 0 && node.children?.length ? [id, ...nested] : nested
  })
}

function close() {
  emit('update:modelValue', false)
}

function toggle(id: string) {
  collapsedIds.value = collapsedIds.value.includes(id)
    ? collapsedIds.value.filter(item => item !== id)
    : [...collapsedIds.value, id]
}

function submit() {
  if (!targetId.value || !props.employees.length || props.loading) return
  emit('confirm', targetId.value)
}
</script>

<template>
  <VDialog
    :model-value="modelValue"
    max-width="560"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <VCard class="pa-6 d-flex flex-column ga-4 bg-surface">
      <div class="d-flex align-center justify-space-between ga-3">
        <span class="text-h4 staff-modal-title">Перемещение сотрудников</span>
        <CustomCloseButton :close-action="close" />
      </div>

      <VMenu
        open-on-hover
        :close-on-content-click="false"
        close-delay="200"
        location="bottom start"
        offset="8"
      >
        <template #activator="{ props: menuProps }">
          <span
            v-bind="menuProps"
            class="text-body-1 text-medium-emphasis cursor-pointer"
          >
            Выбрано сотрудников: {{ employees.length }}
          </span>
        </template>
        <CustomSelectedItems
          :items="selectedItems"
          :title="`Выбрано ${employees.length}:`"
          @remove="emit('remove', String($event))"
          @remove-all="emit('remove-all')"
        />
      </VMenu>

      <div class="d-flex flex-column ga-2">
        <span class="text-body-1 text-medium-emphasis">Куда переместить:</span>
        <div class="move-employees-tree overflow-y-auto">
          <div
            v-for="row in rows"
            :key="row.id"
            class="d-flex align-center ga-1 py-2 px-2 rounded cursor-pointer"
            :class="{ 'move-employees-row--selected': targetId === row.id }"
            :style="{ paddingInlineStart: `${8 + row.depth * 16}px` }"
            @click="targetId = row.id"
          >
            <VIcon
              v-if="row.hasChildren"
              :icon="row.expanded ? 'ri-arrow-down-s-line' : 'ri-arrow-right-s-line'"
              size="20"
              class="flex-shrink-0"
              @click.stop="toggle(row.id)"
            />
            <span v-else class="move-employees-spacer" />
            <span class="text-body-1 text-high-emphasis text-truncate">{{ row.name }}</span>
          </div>

          <div
            class="d-flex align-center ga-1 py-2 px-2 rounded cursor-pointer mt-1 border-t"
            :class="{ 'move-employees-row--selected': targetId === 'without-department' }"
            style="padding-inline-start: 8px;"
            @click="targetId = 'without-department'"
          >
            <span class="move-employees-spacer" />
            <span class="text-body-1 text-high-emphasis text-truncate">Без отделения</span>
          </div>
        </div>
      </div>

      <div class="d-flex justify-end">
        <VBtn
          color="primary"
          :disabled="!targetId || !employees.length"
          :loading="loading"
          @click="submit"
        >
          Переместить
        </VBtn>
      </div>
    </VCard>
  </VDialog>
</template>

<style scoped lang="scss">
.staff-modal-title {
  color: rgba(var(--v-theme-text-primary), var(--v-high-emphasis-opacity));
}

.move-employees-tree {
  max-block-size: 50vh;
}

.move-employees-row--selected {
  background: rgba(var(--v-theme-on-surface), 0.08);
}

.move-employees-spacer {
  flex-shrink: 0;
  inline-size: 20px;
}
</style>
